/**
 * Townsfolk (ชาวบ้าน): the people who live in a zone and go about their day — fishing at dawn,
 * hoeing the paddies, calling customers to the inn, home behind closed doors at night. Each one
 * follows a schedule of stations for the part of the day, greets a passer-by, runs from a fight,
 * and now and then says something (a bark) that fits the hour, the work at hand or the news.
 * The critters (chickens, cats, birds) scatter and fly off when someone comes close.
 * Add a person or a critter here; the world spawns and drives them.
 */
import { DAY_SECONDS, periodAt } from './time';
import { START, type ZoneId } from './zones';
import { ZHUMING_FOLK } from './zhumingFolk';

/** What someone is doing at a station; the renderer shows a pose (and tool) for each. */
export type Activity =
  | 'idle'
  /** Standing at the water's edge with a rod. */
  | 'fish'
  /** Sitting, mending a net across the lap. */
  | 'mend'
  | 'hoe'
  | 'sweep'
  /** Carrying a shoulder pole with two baskets (shown while walking to the station too). */
  | 'haul'
  /** Calling out to passers-by: hawking wares, inviting guests in. */
  | 'call'
  /** Sitting with a cup. */
  | 'drink'
  | 'sit'
  /** Talking with a partner (see `CHATS`). */
  | 'chat'
  /** Trading blows with a partner, no harm meant. */
  | 'spar'
  /** A child chasing the chickens. */
  | 'play'
  /** Ambling about the station. */
  | 'stroll'
  /** Indoors: out of sight until the part of the day changes. */
  | 'home';

/** Dawn 5–7, day 7–18, dusk 18–21, night 21–5 (the sun is up 6–18, data/time.ts). */
export type DayPart = 'dawn' | 'day' | 'dusk' | 'night';

export interface Station {
  /** A landmark of the map (`plaza` is every map's plaza), shifted by `dx`, `dy` tiles. */
  readonly at: string;
  readonly dx?: number;
  readonly dy?: number;
  readonly act: Activity;
  /** Seconds spent here (a random span). */
  readonly dur: readonly [number, number];
  /** Spread of the spot picked around the station (tiles). */
  readonly r?: number;
  /** Direction faced while working (world x, y), e.g. out to sea while fishing. */
  readonly face?: readonly [number, number];
  /** The partner of a chat or a sparring bout (folk id). */
  readonly with?: string;
  /** Carries the shoulder pole on the way here. */
  readonly carry?: boolean;
  /** Holds a lantern (the night watch). */
  readonly lantern?: boolean;
}

export interface FolkLines {
  /** Said to a passer-by who comes close. `{name}` is the player's name. */
  readonly greet: readonly string[];
  /** While at work, by activity. */
  readonly work?: Partial<Record<Activity, readonly string[]>>;
  readonly night?: readonly string[];
  /** Running from a fight or from a red name. */
  readonly scared: readonly string[];
}

export interface FolkDef {
  readonly id: string;
  readonly name: string;
  readonly title: string;
  readonly sprite: string;
  readonly zone: ZoneId;
  /** The door they go in by at night (a landmark). */
  readonly home: string;
  readonly speed: number;
  readonly child?: boolean;
  readonly schedule: Readonly<Record<DayPart, readonly Station[]>>;
  readonly lines: FolkLines;
}

/** Two people talking: who speaks (0 = `a`, 1 = `b`) and what, one line after another. */
export interface ChatDef {
  readonly a: string;
  readonly b: string;
  readonly lines: readonly (readonly [0 | 1, string])[];
}

/** The critters (and the village animals of data/animals.ts, which share their brain). */
export type CritterKind = 'chicken' | 'cat' | 'bird' | 'chick' | 'dog' | 'duck' | 'ox';

export interface CritterSpawn {
  readonly zone: ZoneId;
  readonly kind: CritterKind;
  readonly at: string;
  readonly dx?: number;
  readonly dy?: number;
  readonly n: number;
}

export const CRITTER_NAMES: Record<CritterKind, string> = {
  chicken: 'ไก่',
  cat: 'แมว',
  bird: 'นกกระจอก',
  chick: 'ลูกไก่',
  dog: 'หมา',
  duck: 'เป็ด',
  ox: 'ควาย',
};

/** How quickly the day turns (data/time.ts): one in-game day every 60 real minutes. */
export { DAY_SECONDS };

export function dayPartAt(hour: number): DayPart {
  if (hour >= 5 && hour < 7) return 'dawn';
  if (hour >= 7 && hour < 18) return 'day';
  if (hour >= 18 && hour < 21) return 'dusk';
  return 'night';
}

/** Thai name of the time of day, for the clock on the screen (data/time.ts `TIME_PERIODS`). */
export const hourName = (hour: number): string => periodAt(hour).name;

/** How far around them townsfolk notice someone (tiles). */
export const FOLK = {
  /** A passer-by this close is greeted and looked at. */
  notice: 2.6,
  /** Seconds before greeting the same passer-by again. */
  greetEvery: 40,
  /** A fight this close (or a red name) sends them running. */
  danger: 7,
  /** Barks are only said with the player this close (nobody far off hears them). */
  hearing: 13,
  /** Seconds between barks. */
  barkEvery: [16, 34] as const,
  /** Seconds between the lines of a chat. */
  chatGap: 3.2,
} as const;

const list: FolkDef[] = [
  // ── หมู่บ้านหยานหยู ──
  {
    id: 'hai',
    name: 'ลุงไห่',
    title: 'ชาวประมงเฒ่า',
    sprite: 'fisherOld',
    zone: START,
    home: 'home1',
    speed: 1.2,
    schedule: {
      dawn: [{ at: 'pierWest', act: 'fish', dur: [60, 90], face: [0, -1], carry: true }],
      day: [
        { at: 'pierWest', act: 'fish', dur: [60, 100], face: [0, -1] },
        { at: 'pierEnd', act: 'mend', dur: [40, 60], r: 1 },
        { at: 'pier', act: 'fish', dur: [50, 80], face: [0, -1] },
        { at: 'pierWest', dx: -2, act: 'sit', dur: [25, 40] },
      ],
      dusk: [
        { at: 'inn', dx: 0.8, act: 'chat', dur: [70, 100], with: 'liu' },
        { at: 'inn', dx: 1.4, dy: 1, act: 'drink', dur: [40, 60] },
      ],
      night: [{ at: 'home1', act: 'home', dur: [600, 600] }],
    },
    lines: {
      greet: [
        'อ้าว {name} ตื่นเช้าเชียว',
        'ระวังลื่นบนสะพานนะหลาน',
        'มาตกปลากับลุงไหมล่ะ',
        'วันแรกลุงนึกว่าเจ้าเป็นศพลอยมาติดท่า ดูตอนนี้สิ',
        'ศาลเจ้าบนเนินน่ะ ไหว้ก่อนออกทะเลทุกครั้งนะหลาน คนหยานหยูทำกันมาสามร้อยปี',
      ],
      work: {
        fish: [
          'วันนี้ปลาไม่ค่อยกินเบ็ดเลย…',
          'ลมทะเลแบบนี้ อีกเดี๋ยวฝนมาแน่',
          'เงียบหน่อยๆ ปลาจะตกใจ',
          'สมัยหนุ่มๆ ลุงจับปลากระเบนได้ตัวเท่าเรือ',
          'คลื่นซัดเข้า ปลาก็เข้า คลื่นถอยกลับ ปลาก็ถอย ปู่ลุงสอนมาอย่างนี้',
          'เด็กหยานหยูทุกรุ่นเคยยืนฟันคลื่นเล่น ลุงก็เคย ฟันได้ไม่ถึงครึ่งวันก็เมื่อย',
        ],
        mend: ['แหขาดอีกแล้ว ฉลามหรือไงนะ', 'ทอทีละตา ทีละตา…', 'เรือข้ามฟากมาทีไร ข่าวลือก็มาเต็มลำ'],
        drink: ['สุราร้อนๆ หลังวันยาวๆ ชื่นใจนัก'],
      },
      night: ['ดึกแล้ว กลับบ้านเถอะหลาน', 'ทะเลกลางคืนไม่ใช่ที่ของคนบนบก'],
      scared: ['ใจเย็นๆ! คนแก่หนีไม่ทันนะ!', 'โอ๊ย! อย่ามาแถวนี้!'],
    },
  },
  {
    id: 'yu',
    name: 'เสี่ยวหยู',
    title: 'ลูกชาวประมง',
    sprite: 'fisherKid',
    zone: START,
    home: 'home1',
    speed: 2.6,
    child: true,
    schedule: {
      dawn: [{ at: 'home1', act: 'home', dur: [600, 600] }],
      day: [
        { at: 'home2', dx: -1, dy: 2, act: 'play', dur: [30, 50], r: 2 },
        { at: 'pierEast', dx: -1.5, act: 'fish', dur: [25, 40], face: [0, -1] },
        { at: 'plaza', dx: -3, dy: 2, act: 'stroll', dur: [20, 30], r: 3 },
        { at: 'houseSW', dx: 2, dy: 1.5, act: 'play', dur: [30, 45], r: 2 },
      ],
      dusk: [
        { at: 'plaza', dx: 2, dy: 3, act: 'play', dur: [40, 60], r: 3 },
        { at: 'home1', act: 'home', dur: [600, 600] },
      ],
      night: [{ at: 'home1', act: 'home', dur: [600, 600] }],
    },
    lines: {
      greet: [
        'พี่ๆ! สอนวิชาให้ข้าบ้างสิ!',
        'ว้าว กระบี่จริงเหรอนั่น',
        'พี่ {name} จับไก่ให้ข้าหน่อย!',
        'พี่ขึ้นไปยืนบนกำแพงซ้อมได้ไหม ครูฝึกเฒ่าหวังว่าคนธรรมดาขึ้นไม่ได้',
        'พี่คือคนที่นอนหลับบนสะพานท่าเรือใช่ไหม ข้าเห็นนะ จิ้งจอกของพี่เลียหน้าพี่ด้วย',
      ],
      work: {
        play: [
          'ไก่ตัวนี้วิ่งเร็วชะมัด!',
          'ฮ่าๆ หนีไม่พ้นหรอก!',
          'โตขึ้นข้าจะเป็นจอมยุทธ์!',
          'ข้าเหลาไม้ไผ่เป็นกระบี่แล้วนะ แม่ว่าเหมือนตะเกียบ',
        ],
        fish: ['ทำไมปลาไม่กินเบ็ดข้าเลย…', 'ข้าจะฟันคลื่นให้ขาดเหมือนเด็กในนิทาน! ฮึบ! …เปียกหมดเลย'],
        stroll: ['หอกลองตีตอนเช้ากับตอนค่ำ ข้านับได้สิบสองที'],
      },
      scared: ['แม่จ๋า!', 'ช่วยด้วย!'],
    },
  },
  {
    id: 'liu',
    name: 'นางหลิว',
    title: 'เจ้าของโรงเตี๊ยมลมทะเล',
    sprite: 'innkeeper',
    zone: START,
    home: 'inn',
    speed: 1.5,
    schedule: {
      dawn: [{ at: 'inn', dy: 0.6, act: 'sweep', dur: [40, 60], r: 1.2 }],
      day: [
        { at: 'inn', dx: -0.5, dy: 0.8, act: 'call', dur: [30, 45] },
        { at: 'inn', dy: 1, act: 'sweep', dur: [25, 35], r: 1.5 },
        { at: 'plaza', dx: -2.5, dy: -1.5, act: 'stroll', dur: [20, 30], r: 2 },
        { at: 'inn', dx: -0.5, dy: 0.8, act: 'call', dur: [30, 45] },
      ],
      dusk: [
        { at: 'inn', dx: -0.4, act: 'chat', dur: [70, 100], with: 'hai' },
        { at: 'inn', dx: -0.5, dy: 0.8, act: 'call', dur: [40, 60] },
      ],
      night: [{ at: 'inn', act: 'home', dur: [600, 600] }],
    },
    lines: {
      greet: [
        'ยินดีต้อนรับเจ้าค่ะ แวะพักก่อนไหม',
        'ท่าน {name} หน้าตาเหนื่อยๆ นะเจ้าคะ',
        'ซาลาเปาเพิ่งออกจากซึ้งเลยเจ้าค่ะ',
        'ชั้นบนมองเห็นทั้งอ่าวเลยเจ้าค่ะ ยามเย็นเรือกลับฝั่งงามนัก',
        'คนแปลกหน้ามาพักทุกวันตั้งแต่ศิลาตื่น ฝ่ามือมีแผลกันทุกคน แปลกจริง',
      ],
      work: {
        call: [
          'ซาลาเปาร้อนๆ สุราดอกท้อ เชิญเจ้าค่ะ!',
          'ห้องพักสะอาด ราคาย่อมเยา!',
          'เดินทางไกลมา แวะดื่มชาก่อนเจ้าค่ะ',
          'จะลงใต้ไปลานศิลาหรือเจ้าคะ กินให้อิ่มก่อน ทางไกลนัก',
        ],
        sweep: [
          'ฝุ่นทะเลมาอีกแล้ว…',
          'หน้าร้านต้องสะอาด แขกถึงจะเข้า',
          'ท่านผู้เฒ่ายืนใต้ต้นไทรทั้งวัน ข้าส่งชาไปให้ ท่านไม่เคยดื่มจนเย็นชืด',
        ],
      },
      scared: ['กรี๊ด! อย่าทำลายร้านข้านะ!', 'ใครก็ได้ช่วยที!'],
    },
  },
  {
    id: 'niu',
    name: 'อาหนิว',
    title: 'ชาวนา',
    sprite: 'farmer',
    zone: START,
    home: 'houseSW',
    speed: 1.6,
    schedule: {
      dawn: [{ at: 'fieldsWest', act: 'hoe', dur: [50, 70], r: 2.5, carry: true }],
      day: [
        { at: 'fieldsWest', act: 'hoe', dur: [50, 80], r: 3 },
        { at: 'fieldsWest', dx: 3, dy: -3, act: 'sit', dur: [20, 30] },
        { at: 'fieldsWest', dx: -3, dy: 2, act: 'hoe', dur: [50, 80], r: 2 },
        { at: 'plaza', dx: -5.5, dy: 1, act: 'haul', dur: [15, 20], carry: true },
      ],
      dusk: [
        { at: 'houseSW', dx: 1, dy: 1, act: 'chat', dur: [60, 80], with: 'sun' },
        { at: 'inn', dx: 2, dy: 1.2, act: 'drink', dur: [40, 60] },
      ],
      night: [{ at: 'houseSW', act: 'home', dur: [600, 600] }],
    },
    lines: {
      greet: [
        'ฟ้าฝนปีนี้ดี ข้าวงามแน่',
        'เดินดีๆ อย่าเหยียบต้นกล้าล่ะ',
        'จอมยุทธ์ {name} รึ? ได้ยินชื่อมาบ้าง',
        'ออกประตูนาไปทางตะวันตกเฉียงใต้ระวังหน่อย บนเนินนั่นมีแต่โจร',
      ],
      work: {
        hoe: [
          'ฮึบ! ฮึบ!',
          'ดินแถวนี้ดี ปลูกอะไรก็ขึ้น',
          'ปวดหลังชะมัด…',
          'นาขั้นนี้ปู่ข้าขุด ขั้นล่างปู่ของปู่ข้าขุด นับย้อนไปไม่หมด',
        ],
        sit: ['พักเหนื่อยสักหน่อย'],
        haul: ['หนักแฮะ ผักปีนี้งามจริง'],
      },
      scared: ['โจรมาแล้ว! หนีเร็ว!', 'อย่าเข้ามานะ ข้ามีจอบ!'],
    },
  },
  {
    id: 'sun',
    name: 'ป้าซุน',
    title: 'ชาวนา',
    sprite: 'farmerAunt',
    zone: START,
    home: 'houseSE',
    speed: 1.4,
    schedule: {
      dawn: [{ at: 'fieldsEast', act: 'hoe', dur: [50, 70], r: 2.5 }],
      day: [
        { at: 'fieldsEast', act: 'hoe', dur: [50, 80], r: 3 },
        { at: 'plaza', dx: 4.5, dy: -2.8, act: 'call', dur: [35, 50], carry: true },
        { at: 'fieldsEast', dx: 2, dy: 3, act: 'hoe', dur: [50, 70], r: 2 },
      ],
      dusk: [
        { at: 'houseSW', dx: 2.2, dy: 1, act: 'chat', dur: [60, 80], with: 'niu' },
        { at: 'houseSE', dy: 1, act: 'sweep', dur: [30, 40], r: 1 },
      ],
      night: [{ at: 'houseSE', act: 'home', dur: [600, 600] }],
    },
    lines: {
      greet: [
        'กินข้าวมาหรือยังลูก',
        'ผอมเชียว มาเอาผักป้าไปกินสิ',
        'ลูกบ้านไหนเนี่ย หน้าตาดีจริง',
        'ข้างนอกประตูตะวันออกเป็นป่าสน หมาป่าหอนทั้งคืน อย่าไปคนเดียวนะลูก',
      ],
      work: {
        call: [
          'ผักสดๆ จากนา! กาดขาว หัวไชเท้า!',
          'ซื้อสามแถมหนึ่ง วันนี้วันดี!',
          'ผักไปไหว้ศาลเจ้าก็มีนะ หัวสวยๆ ทั้งนั้น',
        ],
        hoe: ['ต้นนี้งามจริง', 'อีกแปลงเดียว…', 'ปีนี้นกกระจอกน้อย หมาป่าเยอะ โลกกลับหัวแล้ว'],
      },
      scared: ['ตายแล้ว! ตีกันอีกแล้ว!', 'หลบเร็ว หลบ!'],
    },
  },
  {
    id: 'zheng',
    name: 'ลุงเจิ้ง',
    title: 'ยามตีเกราะ',
    sprite: 'watchman',
    zone: START,
    home: 'houseN',
    speed: 1.3,
    schedule: {
      dawn: [{ at: 'houseN', act: 'home', dur: [600, 600] }],
      day: [{ at: 'houseN', act: 'home', dur: [600, 600] }],
      dusk: [
        { at: 'houseN', dy: 1, act: 'sit', dur: [40, 60] },
        { at: 'plaza', dx: 1.5, dy: -3, act: 'idle', dur: [20, 30], lantern: true },
      ],
      night: [
        { at: 'plaza', dx: 1.5, dy: -3, act: 'idle', dur: [8, 14], lantern: true },
        { at: 'inn', dx: 2, dy: 1, act: 'idle', dur: [8, 14], lantern: true },
        { at: 'shore', dx: 6, act: 'idle', dur: [8, 14], lantern: true },
        { at: 'home3', dx: -2, dy: 1, act: 'idle', dur: [8, 14], lantern: true },
        { at: 'gate', dy: -1, act: 'idle', dur: [8, 14], lantern: true },
      ],
    },
    lines: {
      greet: [
        'ดึกดื่นแล้ว ยังไม่นอนอีกหรือท่าน',
        'ระวังตัวด้วย คืนนี้ลมแรง',
        'ข้าเดินยามมายี่สิบปี เพิ่งจะมีปีนี้ที่หมาป่าหอนทุกคืน',
      ],
      work: {
        idle: [
          'อากาศแห้ง ระวังไฟ~',
          'ยามสาม ทุกบ้านปิดประตูลงกลอน~',
          'ปิดไฟ ระวังโจร~',
          'คืนที่ลมเปลี่ยนทิศ ข้าเห็นแสงเรื่อๆ บนลานศาลเจ้า ทั้งที่ไม่มีใครจุดธูป',
        ],
      },
      scared: ['ใครน่ะ! อย่าก่อเรื่องในหมู่บ้าน!'],
    },
  },

  // ── ลานศิลาเก้ากระบี่ ──
  {
    id: 'skyblade',
    name: 'ดาบเดียวพิชิตฟ้า',
    title: 'ศิษย์สำนักยอดหมอก',
    sprite: 'red',
    zone: 'plains',
    home: 'house5',
    speed: 2.4,
    schedule: {
      dawn: [{ at: 'plaza', dx: -4.2, dy: -3.2, act: 'spar', dur: [80, 120], with: 'moonwind' }],
      day: [
        { at: 'plaza', dx: -4.2, dy: -3.2, act: 'spar', dur: [80, 120], with: 'moonwind' },
        { at: 'pavilion', act: 'sit', dur: [30, 45] },
      ],
      dusk: [{ at: 'pavilion', dx: -1, act: 'chat', dur: [80, 120], with: 'moonwind' }],
      night: [{ at: 'house5', act: 'home', dur: [600, 600] }],
    },
    lines: {
      greet: ['ฝีมือเจ้าคงไม่เลว มาประลองกันสักตาไหม', 'ศิษย์ยอดหมอกไม่เคยถอย!'],
      work: { spar: ['ระวัง! กระบี่นี้เร็วนะ!', 'ท่านี้ข้าฝึกมาสามปี!', 'อีกยก!'], sit: ['เหนื่อยแฮะ…'] },
      scared: ['ถอยก่อน ศัตรูแรงเกินไป!'],
    },
  },
  {
    id: 'moonwind',
    name: 'จันทร์เคียงลม',
    title: 'จอมยุทธ์ไร้นาม',
    sprite: 'night',
    zone: 'plains',
    home: 'house5',
    speed: 2.4,
    schedule: {
      dawn: [{ at: 'plaza', dx: -2.8, dy: -3.2, act: 'spar', dur: [80, 120], with: 'skyblade' }],
      day: [
        { at: 'plaza', dx: -2.8, dy: -3.2, act: 'spar', dur: [80, 120], with: 'skyblade' },
        { at: 'plaza', dx: 4, dy: -3, act: 'stroll', dur: [25, 35], r: 2 },
      ],
      dusk: [{ at: 'pavilion', dx: 0.6, act: 'chat', dur: [80, 120], with: 'skyblade' }],
      night: [{ at: 'house5', act: 'home', dur: [600, 600] }],
    },
    lines: {
      greet: ['…', 'ยุทธภพกว้างใหญ่ เจอกันอีกแล้วนะ', 'ลมคืนนี้หนาว'],
      work: { spar: ['ช้าไป', 'เปิดช่องโหว่อีกแล้ว', 'หึ'] },
      scared: ['ไม่ใช่เวลาสู้'],
    },
  },
  {
    id: 'mist',
    name: 'หมอกม่วงพันลี้',
    title: 'ธิดาหุบเขาบุปผา',
    sprite: 'lilac',
    zone: 'plains',
    home: 'house13',
    speed: 2,
    schedule: {
      dawn: [{ at: 'pavilion', act: 'sit', dur: [60, 90] }],
      day: [
        { at: 'plaza', dx: -3.8, dy: -1.4, act: 'idle', dur: [30, 45], face: [0, -1] },
        { at: 'plaza', dx: 4.6, dy: -2.4, act: 'chat', dur: [50, 70], with: 'mei' },
        { at: 'plaza', act: 'stroll', dur: [25, 35], r: 4 },
      ],
      dusk: [{ at: 'pavilion', dx: 1.5, dy: 0.5, act: 'drink', dur: [80, 120] }],
      night: [{ at: 'house13', act: 'home', dur: [600, 600] }],
    },
    lines: {
      greet: ['ท่านเองหรือ {name}', 'ดอกไม้ที่หุบเขาบานแล้วนะ', 'อย่ามองนานนักสิ'],
      work: {
        idle: ['สองคนนั้นประลองกันทุกวันเลย', 'ท่าของจันทร์เคียงลมงดงามจริง'],
        drink: ['ชาที่นี่หอมดี'],
      },
      scared: ['ต้องหลีกไปก่อนแล้ว'],
    },
  },
  {
    id: 'mei',
    name: 'ป้าเหมย',
    title: 'แม่ค้าน้ำชา',
    sprite: 'teaSeller',
    zone: 'plains',
    home: 'house1',
    speed: 1.4,
    schedule: {
      dawn: [{ at: 'plaza', dx: 5, dy: -2.4, act: 'sweep', dur: [40, 60], r: 1 }],
      day: [
        { at: 'plaza', dx: 5, dy: -2.4, act: 'call', dur: [40, 60] },
        { at: 'plaza', dx: 5.4, dy: -2.4, act: 'chat', dur: [50, 70], with: 'mist' },
        { at: 'plaza', dx: 5, dy: -2.4, act: 'sweep', dur: [20, 30], r: 1 },
      ],
      dusk: [{ at: 'plaza', dx: 5, dy: -2.4, act: 'call', dur: [80, 120] }],
      night: [{ at: 'house1', act: 'home', dur: [600, 600] }],
    },
    lines: {
      greet: [
        'ชาดอกเก๊กฮวยไหมพ่อหนุ่ม',
        'นั่งพักก่อนสิ ขาเดินมาไกล',
        'ท่าน {name} วันนี้ได้ของดีมาหรือเปล่า',
      ],
      work: {
        call: ['ชาร้อนๆ ชื่นใจ! ถ้วยละสองอีแปะ!', 'ชาอู่หลงจากเขายอดหมอก!'],
        sweep: ['ใบไม้ร่วงอีกแล้ว'],
      },
      scared: ['โต๊ะชาข้า! อย่าล้มนะ!'],
    },
  },
  {
    id: 'bao',
    name: 'เสี่ยวเป่า',
    title: 'เด็กซน',
    sprite: 'townKid',
    zone: 'plains',
    home: 'house1',
    speed: 2.6,
    child: true,
    schedule: {
      dawn: [{ at: 'house1', act: 'home', dur: [600, 600] }],
      day: [
        { at: 'house1', dx: 2, dy: 2, act: 'play', dur: [35, 55], r: 2 },
        { at: 'plaza', dx: -2.4, dy: -1.3, act: 'idle', dur: [25, 35], face: [0, -1] },
        { at: 'house20', dx: 1, dy: 2, act: 'play', dur: [30, 45], r: 2 },
      ],
      dusk: [
        { at: 'plaza', dx: 1, dy: 4, act: 'play', dur: [40, 60], r: 3 },
        { at: 'house1', act: 'home', dur: [600, 600] },
      ],
      night: [{ at: 'house1', act: 'home', dur: [600, 600] }],
    },
    lines: {
      greet: ['เย้! จอมยุทธ์มาแล้ว!', 'พี่ {name} บินได้ไหม!', 'ขอจับกระบี่หน่อยได้ไหม'],
      work: { play: ['จับได้แล้ว! อ๊ะ หลุด!', 'ไก่บ้าเอ๊ย!'], idle: ['ฟึ่บ! ฟั่บ! ข้าก็ทำได้!'] },
      scared: ['ฮือ! แม่!'],
    },
  },
  {
    id: 'lao',
    name: 'ผู้กองเหลา',
    title: 'ยามเฝ้าเมือง',
    sprite: 'watchman',
    zone: 'plains',
    home: 'pagoda',
    speed: 1.5,
    schedule: {
      dawn: [{ at: 'pagoda', act: 'home', dur: [600, 600] }],
      day: [{ at: 'pagoda', act: 'home', dur: [600, 600] }],
      dusk: [{ at: 'plaza', dx: 3, dy: 3, act: 'idle', dur: [40, 60], lantern: true }],
      night: [
        { at: 'plaza', dx: 3, dy: 3, act: 'idle', dur: [8, 14], lantern: true },
        { at: 'pavilion', dx: 2, act: 'idle', dur: [8, 14], lantern: true },
        { at: 'house20', dx: 4, dy: 1, act: 'idle', dur: [8, 14], lantern: true },
        { at: 'plaza', dx: -3, dy: -4, act: 'idle', dur: [8, 14], lantern: true },
        { at: 'house1', dx: 3, dy: 1, act: 'idle', dur: [8, 14], lantern: true },
      ],
    },
    lines: {
      greet: ['ดึกแล้ว เดินทางระวังด้วย', 'ในเมืองห้ามชักอาวุธนะ'],
      work: { idle: ['อากาศแห้ง ระวังไฟ~', 'ยามสาม ทุกบ้านปิดประตู~'] },
      scared: ['ใครกล้าก่อความวุ่นวาย!'],
    },
  },
];

export const FOLK_LIST: readonly FolkDef[] = [...list, ...ZHUMING_FOLK];
export const FOLKS: Readonly<Record<string, FolkDef>> = Object.fromEntries(FOLK_LIST.map((f) => [f.id, f]));

export const CHATS: readonly ChatDef[] = [
  {
    a: 'hai',
    b: 'liu',
    lines: [
      [1, 'วันนี้ได้ปลาบ้างไหมลุง'],
      [0, 'สามตัว แต่ตัวใหญ่หลุดไป'],
      [1, 'ทุกวันก็ตัวใหญ่หลุดไปนั่นแหละ'],
      [0, 'คราวนี้จริงๆ นะ ใหญ่เท่าเรือ!'],
      [1, 'ฮ่าๆ เอาเถอะ สุราร้อนรออยู่'],
      [0, 'แขกเจ้าคืนนี้เต็มอีกหรือ'],
      [1, 'เต็มทุกคืนตั้งแต่ศิลาตื่น คนแปลกหน้ามาจากเรือข้ามฟากทุกเที่ยว'],
      [0, 'ท่านผู้เฒ่าบอกว่าศิลาเลือกคนมาหลายคน ไม่ใช่คนเดียว'],
      [1, 'งั้นข้าต้องนึ่งซาลาเปาเพิ่มแล้วสิ'],
    ],
  },
  {
    a: 'niu',
    b: 'sun',
    lines: [
      [0, 'ป้าซุน นาฝั่งตะวันออกเป็นไงบ้าง'],
      [1, 'งามดี แต่หมาป่าลงมาขุดคันนาอีกแล้ว'],
      [0, 'ต้องไปบอกผู้เฒ่าหลิวแล้ว'],
      [1, 'บอกแล้ว ท่านว่าจะหาจอมยุทธ์มาช่วย'],
      [0, 'ฝั่งข้าก็โดนโจรงัดยุ้ง ประตูนาต้องลงกลอนสองชั้นแล้ว'],
      [1, 'สมัยแม่ป้า ประตูนาไม่เคยต้องปิดเลยนะ'],
    ],
  },
  {
    a: 'skyblade',
    b: 'moonwind',
    lines: [
      [0, 'พรุ่งนี้ข้าต้องชนะเจ้าให้ได้'],
      [1, 'เจ้าพูดแบบนี้มาสามสิบวันแล้ว'],
      [0, 'วันที่สามสิบเอ็ดจะต่างออกไป!'],
      [1, 'หึ… หวังว่าอย่างนั้น'],
    ],
  },
  {
    a: 'mist',
    b: 'mei',
    lines: [
      [1, 'ชาดอกไม้ตามเคยไหมคุณหนู'],
      [0, 'เจ้าค่ะ ใส่น้ำผึ้งด้วย'],
      [1, 'ได้ยินว่ามีปีศาจออกอาละวาดทางเหนือ'],
      [0, 'คงมีจอมยุทธ์ไปจัดการแล้วกระมัง'],
    ],
  },
];

/** The chat a person leads (as `a`) with this partner, if any. */
export const chatOf = (a: string, b: string): ChatDef | undefined =>
  CHATS.find((c) => c.a === a && c.b === b);

/** What everyone talks about: the quest under way, the world boss, a disciple of a school. */
export const GOSSIP = {
  quest: {
    'three-forms': [
      'ลานฝึกของหยานหยูอยู่ทางตะวันตก หลังโรงเตี๊ยม',
      'ศิษย์คู่ซ้อมที่ลานฝึกไม่ทำใครเจ็บหรอก แค่เจ็บใจ',
    ],
    bandits: [
      'ได้ยินว่าโจรป่าทางใต้ปล้นเกวียนอีกแล้ว',
      'พวกโจรนั่นใจกล้าขึ้นทุกวัน',
      'ที่หยานหยู โจรตั้งค่ายบนเนินนอกประตูนา ทางตะวันตกเฉียงใต้',
    ],
    wolves: [
      'หมาป่าลงมาจากเขาตะวันออก ระวังตัวด้วย',
      'เมื่อคืนได้ยินเสียงหมาป่าหอนใกล้หมู่บ้าน',
      'นอกประตูตะวันออกของหยานหยูเป็นป่าสน หมาป่าเงาจันทร์ทำรังอยู่ในนั้น',
    ],
    'black-sword': [
      'ว่ากันว่ากระบี่ทมิฬตื่นแล้ว',
      'อย่าเข้าใกล้ลานเลือดทางใต้นะ',
      'สามคืนแล้วที่มีแสงแดงขึ้นจากลานเลือด เหมือนเมื่อยี่สิบปีก่อน',
    ],
    monks: ['ได้ยินว่าวัดร้างหลังเขาพันบันไดมีพระแปลกหน้ามาอยู่', 'หินระฆังยังเก็บกระบี่ระฆังเหล็กของเถาฉางเยี่ยไว้นะ'],
    jiangshi: ['สุสานใต้ยอดหมอกมีศพลุกขึ้นมาเดิน', 'ผนึกของกระบี่ลายเมฆาคงอ่อนแรงลงแล้ว'],
    'thousand-swords': ['ทางเหนือมีกระบี่ร่วงจากฟ้าเหมือนฝน', 'ใครปราบจอมมารได้ คงเป็นเถาฉางเยี่ยคนใหม่'],
  } as Readonly<Record<string, readonly string[]>>,
  boss: ['ได้ยินไหม ปีศาจตนนั้นออกอาละวาดทางเหนือ', 'จอมยุทธ์หลายคนไปปราบปีศาจแล้วไม่กลับมา'],
  /** `{school}` is the player's school. */
  school: ['ศิษย์{school}นี่เอง สง่างามจริง', 'ได้ยินว่าวิชาของ{school}ลึกล้ำนัก'],
  /** To a player with an evil name. */
  evil: ['คนผู้นี้ไอสังหารแรงนัก…', 'อย่าไปสบตาเขาเชียว'],
  night: ['ดึกแล้ว อย่าเดินคนเดียวนักเลย', 'คืนนี้เดือนสวยจริง'],
} as const;

/** The townsfolk's words for the key people (elder, merchant, guide), who stay at their posts. */
export const NPC_LINES: Readonly<Record<'elder' | 'merchant' | 'guide' | 'master', FolkLines>> = {
  elder: {
    greet: ['มาแล้วหรือ {name}', 'ฝึกฝนวิชาอย่าได้ขาด', 'ยุทธภพไม่เคยสงบนาน…'],
    work: {
      idle: [
        'ศิลาเก้ากระบี่ยังเงียบงัน…',
        'ลมเปลี่ยนทิศแล้ว',
        'แปดร่องว่าง ร่องที่เก้าไม่เคยมีกระบี่…',
        'อู๋ซิน… เจ้าได้ยินเสียงอะไรจากใต้หินกันแน่',
        'ตระกูลหลิวเฝ้าศิลามาหลายชั่วคน ไม่มีชั่วคนไหนได้นอนเต็มอิ่ม',
      ],
    },
    scared: [],
  },
  merchant: {
    greet: [
      'เชิญชมสินค้าก่อนท่าน!',
      'ของดีราคาถูก มีแต่ร้านข้า!',
      'ท่าน {name} มีของมาขายไหม',
      'ปลา แร่ สมุนไพร ฝ้าย ข้ารับซื้อหมด ค้าขายต้องมีสัจจะ',
    ],
    work: {
      idle: [
        'ยาสมานแผลมาใหม่!',
        'ใครจะซื้อกระบี่ ข้ามีหลายเล่ม!',
        'ตั้งแต่ศิลาตื่น ยาสมานแผลขายดีกว่าข้าวสาร',
      ],
      sit: ['ดึกแล้ว แต่ร้านข้ายังเปิดนะ'],
    },
    scared: [],
  },
  guide: {
    greet: ['จะไปไหนบอกข้าได้', 'เส้นทางขึ้นเขาข้ารู้ทุกซอก'],
    work: { idle: ['หิมะบนยอดเขาละลายช้าปีนี้', 'ทางทุกสายในยุทธภพ ข้าเดินมาแล้วอย่างน้อยสองรอบ'] },
    scared: [],
  },
  master: {
    greet: ['อยากเข้าสำนักหรือ', 'วิชาไม่ได้มาจากฟ้า ต้องฝึกเอง'],
    work: { idle: ['ศิษย์รุ่นนี้ยังอ่อนนัก'] },
    scared: [],
  },
};

export const CRITTERS: readonly CritterSpawn[] = [
  { zone: START, kind: 'chicken', at: 'houseSW', dx: 2, dy: 1.5, n: 4 },
  { zone: START, kind: 'chicken', at: 'home2', dx: -1, dy: 2, n: 3 },
  { zone: START, kind: 'cat', at: 'inn', dx: 2.5, dy: 0.4, n: 1 },
  { zone: START, kind: 'cat', at: 'pavilion', dx: 1.5, n: 1 },
  { zone: START, kind: 'bird', at: 'plaza', dx: 3, dy: -3, n: 4 },
  { zone: START, kind: 'bird', at: 'fieldsWest', dx: 2, dy: -4, n: 3 },
  { zone: START, kind: 'bird', at: 'fieldsEast', dx: -2, dy: -4, n: 3 },
  { zone: 'plains', kind: 'chicken', at: 'house1', dx: 2, dy: 2, n: 4 },
  { zone: 'plains', kind: 'chicken', at: 'house20', dx: 1, dy: 2, n: 3 },
  { zone: 'plains', kind: 'cat', at: 'pavilion', dx: -1.5, n: 1 },
  { zone: 'plains', kind: 'cat', at: 'plaza', dx: 5.5, dy: -3.4, n: 1 },
  { zone: 'plains', kind: 'bird', at: 'plaza', dx: 3, dy: 4, n: 5 },
  { zone: 'plains', kind: 'bird', at: 'pagoda', dx: -2, dy: 2, n: 3 },
];
